'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from 'react';
import { getColorCssVarWithAlpha, getCssColorBasedOnPosition } from '../../services/color-service';
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  baseAlpha: number;
};
type Cursor = {
  x: number;
  y: number;
};

// Helper to calculate particle count based on canvas size
function calculateParticleCount(width: number, height: number, density: number = 0.0005, maxParticles: number = 3000): number {
  const count = Math.floor(width * height * density);
  return Math.min(count, maxParticles);
}
function getRandomNumber(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}
const NightParticleCanvas: React.FC = () => {
  const $ = _c(4);
  const canvasRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const particles = useRef(t0);
  const isMouseDown = useRef(false);
  const particleCount = useRef(0);
  const animationFrameId = useRef(null);
  const resizeRaf = useRef(null);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const canvas = canvasRef.current;
      const cursor = {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      };
      const ctx = canvas.getContext("2d");
      const initializeCanvas = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        particleCount.current = calculateParticleCount(canvas.width, canvas.height, 0.0005, 3000);
        particles.current = Array.from({
          length: particleCount.current
        }, () => ({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          radius: Math.random() * 2 + 1,
          baseAlpha: 0.1
        }));
      };
      initializeCanvas();
      const handleMouseMove = e => {
        cursor.x = e.clientX;
        cursor.y = e.clientY;
      };
      const handleTouchMove = e_0 => {
        if (e_0.touches.length > 0) {
          cursor.x = e_0.touches[0].clientX;
          cursor.y = e_0.touches[0].clientY;
        }
      };
      const handleMouseDown = () => {
        isMouseDown.current = true;
      };
      const handleMouseUp = () => {
        isMouseDown.current = false;
      };
      const handleTouchStart = e_1 => {
        isMouseDown.current = true;
        if (e_1.touches.length > 0) {
          cursor.x = e_1.touches[0].clientX;
          cursor.y = e_1.touches[0].clientY;
        }
      };
      const handleTouchEnd = () => {
        isMouseDown.current = false;
      };
      const handleResize = () => {
        if (resizeRaf.current !== null) {
          return;
        }
        resizeRaf.current = window.requestAnimationFrame(() => {
          resizeRaf.current = null;
          initializeCanvas();
        });
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mousedown", handleMouseDown);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchstart", handleTouchStart);
      window.addEventListener("touchend", handleTouchEnd);
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("resize", handleResize);
      cursor.x = window.innerWidth / 2;
      cursor.y = window.innerHeight / 2;
      isMouseDown.current = true;
      setTimeout(() => {
        isMouseDown.current = false;
      }, 1200);
      const animate = () => {
        ctx.fillStyle = getColorCssVarWithAlpha("--bg", 0.6);
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        for (const p of particles.current) {
          const dx = cursor.x - p.x;
          const dy = cursor.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const ux = dx / dist;
          const uy = dy / dist;
          const tangentX = -uy;
          const tangentY = ux;
          const swirlStrength = Math.min(100 / dist, 4);
          const attractionStrength = 100 / dist;
          if (isMouseDown.current) {
            const repulsionStrength = Math.min(200 / dist, 8);
            p.vx = p.vx - ux * repulsionStrength * 0.05;
            p.vy = p.vy - uy * repulsionStrength * 0.05;
          } else {
            p.vx = p.vx + ux * attractionStrength * 0.0003;
            p.vy = p.vy + uy * attractionStrength * 0.0003;
            p.vx = p.vx + tangentX * swirlStrength * 0.02;
            p.vy = p.vy + tangentY * swirlStrength * 0.02;
          }
          p.x = p.x + p.vx;
          p.y = p.y + p.vy;
          p.vx = p.vx * 0.92;
          p.vy = p.vy * 0.92;
          if (p.x < 0 || p.x > canvas.width || p.y < 0 || p.y > canvas.height) {
            const towardCenterX = centerX - p.x;
            const towardCenterY = centerY - p.y;
            p.x = p.x + towardCenterX * getRandomNumber(0.1, 0.8);
            p.y = p.y + towardCenterY * getRandomNumber(0.1, 0.8);
            p.baseAlpha = 0;
          }
          if (p.baseAlpha < 0.1) {
            p.baseAlpha = p.baseAlpha + 0.005;
          }
          const alpha = Math.min(1, p.baseAlpha + 100 / dist * 0.5);
          const glowColor = getCssColorBasedOnPosition("--surface-2", alpha * 0.5, p.x, p.y);
          const solidColor = getCssColorBasedOnPosition("--surface-4", alpha, p.x, p.y);
          ctx.save();
          if (alpha < 0.5) {
            ctx.shadowBlur = 50;
            ctx.shadowColor = glowColor;
            ctx.fillStyle = glowColor;
          }
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius * 1.4, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          ctx.fillStyle = solidColor;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.fill();
        }
        animationFrameId.current = requestAnimationFrame(animate);
      };
      animationFrameId.current = requestAnimationFrame(animate);
      const handleVisibility = () => {
        if (document.hidden) {
          if (animationFrameId.current !== null) {
            cancelAnimationFrame(animationFrameId.current);
            animationFrameId.current = null;
          }
        } else {
          if (animationFrameId.current === null) {
            animationFrameId.current = requestAnimationFrame(animate);
          }
        }
      };
      document.addEventListener("visibilitychange", handleVisibility);
      return () => {
        if (animationFrameId.current !== null) {
          cancelAnimationFrame(animationFrameId.current);
        }
        if (resizeRaf.current !== null) {
          cancelAnimationFrame(resizeRaf.current);
        }
        document.removeEventListener("visibilitychange", handleVisibility);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mousedown", handleMouseDown);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchend", handleTouchEnd);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("resize", handleResize);
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <canvas ref={canvasRef} className="pointer-events-none fixed top-0 left-0 -z-10 h-dvh w-dvw" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
export default NightParticleCanvas;